import React, { useContext } from 'react';
import {
  CircularProgress,
  FormHelperText,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import moment from 'moment';
import Joi from 'joi';
import { Button } from '@fashionnovateam/nova-ui-toolkit';
import TableCell from '../../ReusableComponents/Table/TableCell';
import countries from '../../../Lib/Constants/countries';
import { ItemLeadTime, ItemLeadTimeErrors, EditedItemLeadTime } from '../types';
import styles from './RequestedItems.module.css';
import { useSubmitItemLeadTime } from '../../../Lib/Hooks/item-lead-time.hooks';
import { FieldsValidationSchema, UpdateOneSchema } from '../validation';
import { FeatureFlagsContext } from '../../../Lib/Contexts/FeatureFlagsContext';
import { normalizeSubmissionData } from '../utils/normalizeSubmissionData';
import { shipmentModeOptions } from '../constants';

interface Props {
  userId: string;
  itemLeadTime: ItemLeadTime;
  ready: boolean;
  loading: boolean;
  domesticEligible?: boolean;
  onUpdate: (item: EditedItemLeadTime) => void;
  handleItemValidation: (item: ItemLeadTime) => void;
  errors: ItemLeadTimeErrors;
  setError: (worksheetId: string, itemErrors: ItemLeadTimeErrors) => void;
  perms: { canSubmit: boolean; canUpdate: boolean };
}

export const exclusivityOptions = [
  { value: 'Exclusive Going Forward', text: 'Exclusive Going Forward' },
  { value: 'FN Exclusive', text: 'Always FN Exclusive' },
  { value: 'Not Exclusive', text: 'Not Exclusive' },
];

export { shipmentModeOptions } from '../constants';

const selectClass = (ready: boolean) =>
  ready ? styles.readyItemColumnText : styles.itemColumnText;

function RequestedItemTableRowEditable({
  userId,
  itemLeadTime,
  ready,
  loading: globalLoading,
  domesticEligible,
  onUpdate,
  handleItemValidation,
  errors,
  setError,
  perms,
}: Props) {
  const { mutate, isLoading: localLoading } = useSubmitItemLeadTime();
  const loading = globalLoading || localLoading;
  const { flags } = useContext(FeatureFlagsContext);

  const handleError = (joiResult: Joi.ValidationResult) => {
    if (!joiResult.error) return;
    setError(
      itemLeadTime.worksheet_id,
      joiResult.error.details.reduce((acc, detail) => {
        if (detail.context?.key) {
          return {
            ...acc,
            [detail.context.key]: {
              isInvalid: true,
              message: detail.message,
            },
          };
        }
        return acc;
      }, {}),
    );
  };

  const handleSubmit = () => {
    const result = UpdateOneSchema.validate(itemLeadTime, {
      abortEarly: false,
      stripUnknown: true,
    });
    if (result.error) {
      handleError(result);
      return;
    }

    const transformedData = normalizeSubmissionData(result.value);

    mutate({
      userId,
      worksheetId: itemLeadTime.worksheet_id,
      data: transformedData,
    });
  };

  const handleOnChangeError = (
    key: string,
    joiResult: Joi.ValidationResult,
  ) => {
    if (joiResult.error) {
      setError(itemLeadTime.worksheet_id, {
        ...errors,
        [key]: { message: joiResult.error.details[0].message, isInvalid: true },
      });
    } else {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [key]: removed, ...newErrors } = errors;
      setError(itemLeadTime.worksheet_id, newErrors);
    }
  };

  const handleOnChange = (key: string, value: string | number) => {
    onUpdate({
      worksheet_id: itemLeadTime.worksheet_id,
      [key]: value === '' ? null : value,
    });
  };

  const handleValidation = (
    key: string,
    value: string,
    validationSchema: Joi.Schema,
  ) => {
    handleOnChangeError(
      key,
      validationSchema.validate(value, { abortEarly: false }),
    );
    handleItemValidation({
      ...itemLeadTime,
      [key]: value === '' ? null : value,
    });
  };

  return (
    <>
      <TableCell
        padding="normal"
        align="left"
        className={`${selectClass(ready)} ${styles.readyStickyItemColumn} ${styles.baseCell}`}
      >
        {itemLeadTime.style_color}
      </TableCell>
      <TableCell
        padding="normal"
        align="left"
        className={`${selectClass(ready)} ${styles.baseCell}`}
      >
        {moment(itemLeadTime.requested_date).format('MM/DD/YYYY')}
      </TableCell>
      <TableCell padding="normal" align="left" className={selectClass(ready)}>
        <TextField
          id="outlined-basic"
          variant="outlined"
          className={styles.input}
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          }}
          disabled={!perms.canUpdate || loading}
          value={itemLeadTime.cost_jr ?? ''}
          onBlur={(e) => {
            handleOnChange('cost_jr', Number(e.target.value) || 0);
            handleValidation(
              'cost_jr',
              e.target.value,
              FieldsValidationSchema.cost_jr,
            );
          }}
          onChange={(e) => {
            onUpdate({
              worksheet_id: itemLeadTime.worksheet_id,
              cost_jr: e.target.value,
            });
          }}
          error={errors.cost_jr?.isInvalid}
          helperText={errors.cost_jr?.message}
        />
      </TableCell>
      <TableCell className={selectClass(ready)}>
        <TextField
          id="outlined-basic"
          variant="outlined"
          className={styles.input}
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          }}
          disabled={!perms.canUpdate || loading}
          value={itemLeadTime.cost_plus ?? ''}
          onBlur={(e) => {
            handleOnChange('cost_plus', Number(e.target.value) || 0);
            handleValidation(
              'cost_plus',
              e.target.value,
              FieldsValidationSchema.cost_plus,
            );
          }}
          onChange={(e) => {
            onUpdate({
              worksheet_id: itemLeadTime.worksheet_id,
              cost_plus: e.target.value,
            });
          }}
          error={errors.cost_plus?.isInvalid}
          helperText={errors.cost_plus?.message}
        />
      </TableCell>
      {flags?.baseline_cost?.enabled && (
        <>
          <TableCell className={selectClass(ready)}>
            <TextField
              id="outlined-baseline-cost-jr"
              variant="outlined"
              className={styles.input}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
                className: 'bg-gray-100 text-gray-500',
                readOnly: true,
              }}
              disabled
              value={itemLeadTime.baseline_cost_jr ?? ''}
            />
          </TableCell>
          <TableCell className={selectClass(ready)}>
            <TextField
              id="outlined-baseline-cost-plus"
              variant="outlined"
              className={styles.input}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
                className: 'bg-gray-100 text-gray-500',
                readOnly: true,
              }}
              disabled
              value={itemLeadTime.baseline_cost_plus ?? ''}
            />
          </TableCell>
        </>
      )}
      <TableCell className={selectClass(ready)}>
        <Select
          className={styles.input}
          style={{ width: '100%' }}
          disabled={!perms.canUpdate || loading}
          value={itemLeadTime.country_of_origin || ''}
          onChange={(e) => {
            handleOnChange('country_of_origin', e.target.value);
          }}
          onBlur={(e) => {
            handleValidation(
              'country_of_origin',
              e.target.value,
              FieldsValidationSchema.country_of_origin,
            );
          }}
          error={errors.country_of_origin?.isInvalid}
        >
          {countries.map((country) => (
            <MenuItem
              key={country.text}
              value={country.text}
              disabled={!domesticEligible && country.value === 'US'}
            >
              {country.text}
            </MenuItem>
          ))}
        </Select>
        {errors.country_of_origin?.message && (
          <FormHelperText error>
            {errors.country_of_origin?.message}
          </FormHelperText>
        )}
      </TableCell>
      <TableCell className={selectClass(ready)}>
        <TextField
          variant="standard"
          type="date"
          disabled={!perms.canUpdate || loading}
          value={
            itemLeadTime.earliest_custom_cut_delivery_date
              ? moment
                  .utc(itemLeadTime.earliest_custom_cut_delivery_date)
                  .format('YYYY-MM-DD')
              : ''
          }
          inputProps={{
            min: moment().format('YYYY-MM-DD'),
            max: '9999-12-31',
            onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
              handleOnChange(
                'earliest_custom_cut_delivery_date',
                e.target.value,
              );
            },
            onBlur: (e) => {
              handleValidation(
                'earliest_custom_cut_delivery_date',
                e.target.value,
                FieldsValidationSchema.earliest_custom_cut_delivery_date,
              );
            },
          }}
          error={errors.earliest_custom_cut_delivery_date?.isInvalid}
          helperText={errors.earliest_custom_cut_delivery_date?.message}
        />
      </TableCell>
      <TableCell className={selectClass(ready)}>
        <TextField
          type="number"
          variant="outlined"
          className={styles.input}
          disabled={!perms.canUpdate || loading}
          value={itemLeadTime.custom_cut_lead_time_in_weeks ?? ''}
          InputProps={{
            endAdornment: <InputAdornment position="end">weeks</InputAdornment>,
          }}
          onBlur={(e) => {
            handleOnChange(
              'custom_cut_lead_time_in_weeks',
              e.target.value ? Number(e.target.value) : '',
            );
            handleValidation(
              'custom_cut_lead_time_in_weeks',
              e.target.value,
              FieldsValidationSchema.custom_cut_lead_time_in_weeks,
            );
          }}
          onChange={(e) => {
            onUpdate({
              worksheet_id: itemLeadTime.worksheet_id,
              custom_cut_lead_time_in_weeks: Number(e.target.value),
            });
          }}
          error={errors.custom_cut_lead_time_in_weeks?.isInvalid}
          helperText={errors.custom_cut_lead_time_in_weeks?.message}
        />
      </TableCell>
      <TableCell className={selectClass(ready)}>
        <Select
          className={styles.input}
          style={{ width: '100%' }}
          disabled={!perms.canUpdate || loading}
          value={itemLeadTime.shipment_mode || ''}
          onChange={(e) => {
            handleOnChange('shipment_mode', e.target.value);
          }}
          onBlur={(e) => {
            handleValidation(
              'shipment_mode',
              e.target.value,
              FieldsValidationSchema.shipment_mode,
            );
          }}
          error={errors.shipment_mode?.isInvalid}
        >
          {shipmentModeOptions.map(({ value, text }) => (
            <MenuItem key={value} value={value}>
              {text}
            </MenuItem>
          ))}
        </Select>
        {errors.shipment_mode?.message && (
          <FormHelperText error>{errors.shipment_mode?.message}</FormHelperText>
        )}
      </TableCell>
      <TableCell className={selectClass(ready)}>
        <Select
          className={styles.input}
          style={{ width: '100%' }}
          disabled={!perms.canUpdate || loading}
          value={itemLeadTime.exclusivity || 'Not Exclusive'}
          onChange={(e) => {
            handleOnChange('exclusivity', e.target.value);
          }}
          onBlur={(e) => {
            handleValidation(
              'exclusivity',
              e.target.value,
              FieldsValidationSchema.exclusivity,
            );
          }}
          error={errors.exclusivity?.isInvalid}
        >
          {exclusivityOptions.map(({ value, text }) => (
            <MenuItem key={value} value={value}>
              {text}
            </MenuItem>
          ))}
        </Select>
        {errors.exclusivity?.message && (
          <FormHelperText error>{errors.exclusivity?.message}</FormHelperText>
        )}
      </TableCell>
      <TableCell className={selectClass(ready)}>
        <TextField
          id="outlined-basic"
          variant="outlined"
          className={styles.input}
          disabled={!perms.canUpdate || loading}
          value={itemLeadTime.stock_quantity ?? ''}
          onChange={(e) => {
            handleOnChange('stock_quantity', Number(e.target.value) || 0);
          }}
          onBlur={(e) => {
            handleValidation(
              'stock_quantity',
              e.target.value,
              FieldsValidationSchema.stock_quantity,
            );
          }}
          error={errors.stock_quantity?.isInvalid}
          helperText={errors.stock_quantity?.message}
        />
      </TableCell>
      <TableCell className={selectClass(ready)}>
        <TextField
          id="outlined-basic"
          variant="outlined"
          className={styles.input}
          disabled={!perms.canUpdate || loading}
          value={itemLeadTime.stock_ratio || ''}
          onChange={(e) => {
            handleOnChange('stock_ratio', e.target.value);
          }}
          onBlur={(e) => {
            handleValidation(
              'stock_ratio',
              e.target.value,
              FieldsValidationSchema.stock_ratio,
            );
          }}
          error={errors.stock_ratio?.isInvalid}
          helperText={errors.stock_ratio?.message}
        />
      </TableCell>
      <TableCell className={selectClass(ready)}>
        <TextField
          variant="standard"
          type="date"
          disabled={!perms.canUpdate || loading}
          value={
            itemLeadTime.earliest_IHD_date_to_deliver_stock
              ? moment
                  .utc(itemLeadTime.earliest_IHD_date_to_deliver_stock)
                  .format('YYYY-MM-DD')
              : ''
          }
          inputProps={{
            min: moment().format('YYYY-MM-DD'),
            max: '9999-12-31',
            onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
              handleOnChange(
                'earliest_IHD_date_to_deliver_stock',
                e.target.value,
              );
            },
            onBlur: (e) => {
              handleValidation(
                'earliest_IHD_date_to_deliver_stock',
                e.target.value,
                FieldsValidationSchema.earliest_IHD_date_to_deliver_stock,
              );
            },
          }}
          error={errors.earliest_IHD_date_to_deliver_stock?.isInvalid}
          helperText={errors.earliest_IHD_date_to_deliver_stock?.message}
        />
      </TableCell>
      <TableCell className={selectClass(ready)}>
        <TextField
          id="outlined-basic"
          className={styles.input}
          variant="outlined"
          disabled={!perms.canUpdate || loading}
          value={itemLeadTime.incoming_stock_quantity ?? ''}
          onChange={(e) => {
            handleOnChange(
              'incoming_stock_quantity',
              Number(e.target.value) || 0,
            );
          }}
          onBlur={(e) => {
            handleValidation(
              'incoming_stock_quantity',
              e.target.value,
              FieldsValidationSchema.incoming_stock_quantity,
            );
          }}
          error={errors.incoming_stock_quantity?.isInvalid}
          helperText={errors.incoming_stock_quantity?.message}
        />
      </TableCell>
      <TableCell className={selectClass(ready)}>
        <TextField
          variant="standard"
          type="date"
          disabled={!perms.canUpdate || loading}
          value={
            itemLeadTime.incoming_stock_date
              ? moment
                  .utc(itemLeadTime.incoming_stock_date)
                  .format('YYYY-MM-DD')
              : ''
          }
          inputProps={{
            min: moment().format('YYYY-MM-DD'),
            max: '9999-12-31',
            onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
              handleOnChange('incoming_stock_date', e.target.value);
            },
            onBlur: (e) => {
              handleValidation(
                'incoming_stock_date',
                e.target.value,
                FieldsValidationSchema.incoming_stock_date,
              );
            },
          }}
          error={errors.incoming_stock_date?.isInvalid}
          helperText={errors.incoming_stock_date?.message}
        />
      </TableCell>
      <TableCell className={selectClass(ready)}>
        <TextField
          id="outlined-basic"
          className={styles.input}
          variant="outlined"
          disabled={!perms.canUpdate || loading}
          value={itemLeadTime.incoming_stock_ratio || ''}
          onChange={(e) => {
            handleOnChange('incoming_stock_ratio', e.target.value);
          }}
          onBlur={(e) => {
            handleValidation(
              'incoming_stock_ratio',
              e.target.value,
              FieldsValidationSchema.incoming_stock_ratio,
            );
          }}
          error={errors.incoming_stock_ratio?.isInvalid}
          helperText={errors.incoming_stock_ratio?.message}
        />
      </TableCell>
      <TableCell className={selectClass(ready)}>
        <TextField
          id="outlined-basic"
          className={styles.input}
          variant="outlined"
          disabled={!perms.canUpdate || loading}
          value={itemLeadTime.custom_MOQ_jr ?? ''}
          onChange={(e) => {
            handleOnChange('custom_MOQ_jr', Number(e.target.value) || 0);
          }}
          onBlur={(e) => {
            handleValidation(
              'custom_MOQ_jr',
              e.target.value,
              FieldsValidationSchema.custom_MOQ_jr,
            );
          }}
          error={errors.custom_MOQ_jr?.isInvalid}
          helperText={errors.custom_MOQ_jr?.message}
        />
      </TableCell>
      <TableCell className={selectClass(ready)}>
        <TextField
          id="outlined-basic"
          className={styles.input}
          variant="outlined"
          disabled={!perms.canUpdate || loading}
          value={itemLeadTime.custom_MOQ_plus ?? ''}
          onChange={(e) => {
            handleOnChange('custom_MOQ_plus', Number(e.target.value) || 0);
          }}
          onBlur={(e) => {
            handleValidation(
              'custom_MOQ_plus',
              e.target.value,
              FieldsValidationSchema.custom_MOQ_plus,
            );
          }}
          error={errors.custom_MOQ_plus?.isInvalid}
          helperText={errors.custom_MOQ_plus?.message}
        />
      </TableCell>
      <TableCell className={selectClass(ready)}>
        <TextField
          id="outlined-basic"
          className={styles.input}
          variant="outlined"
          disabled={!perms.canUpdate || loading}
          value={itemLeadTime.comments || ''}
          onChange={(e) => {
            handleOnChange('comments', e.target.value);
          }}
          onBlur={(e) => {
            handleValidation(
              'comments',
              e.target.value,
              FieldsValidationSchema.comments,
            );
          }}
          error={errors.comments?.isInvalid}
          helperText={errors.comments?.message}
        />
      </TableCell>
      <TableCell className={selectClass(ready)}>
        {perms.canSubmit && (
          <Button
            disabled={loading || !ready}
            onClick={handleSubmit}
            className="w-40 py-6"
          >
            {localLoading ? <CircularProgress /> : 'Submit Item'}
          </Button>
        )}
      </TableCell>
    </>
  );
}

export default RequestedItemTableRowEditable;
